import '@fontsource-variable/inter/wght.css';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { ToastProvider, initGoogleAdsConsentMode } from '@sighthost/ui';
import { App } from './App';
import './globals.css';

// Google Ads Consent Mode v2 defaults (denied) + gtag.js when configured.
// Runs before render so the consent default precedes any tag fire; the
// conversion itself fires from PayInvoicePage / InvoiceDetailPage on a
// CONFIRMED-paid invoice. Inert until siteConfig.googleAds.conversionId is
// set. See CONVERSION_TRACKING_ARCHITECTURE_2026-07-08.md (AD-5).
initGoogleAdsConsentMode();

/**
 * React Router 7 emits a "No HydrateFallback element provided to render
 * during initial hydration" warning even when the app is purely CSR
 * (createRoot, no SSR, no loaders, no lazy routes) and a top-level
 * HydrateFallback IS registered on the root data-router route. This is
 * a known-benign internal warning — the proper-API fixes don't reliably
 * suppress it across RR7 patch versions. Filter it here once at
 * bootstrap so the dev console stays clean.
 *
 * Scope: ONLY this exact message is filtered — every other warning
 * passes through untouched.
 */
const originalConsoleWarn = console.warn;
console.warn = (...args: unknown[]): void => {
  const first = args[0];
  if (
    typeof first === 'string' &&
    first.includes('No `HydrateFallback` element provided')
  ) {
    return;
  }
  originalConsoleWarn(...args);
};

const rootEl = document.getElementById('root');
if (!rootEl) throw new Error('Root element #root not found in index.html');

createRoot(rootEl).render(
  <StrictMode>
    <ToastProvider>
      <App />
    </ToastProvider>
  </StrictMode>
);
